import { useState, useEffect } from 'react';
import { Shield, Cookie, BarChart3, Sparkles, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useConsent } from '@/contexts/ConsentContext';
import { toast } from 'sonner';

interface CookiePreferencesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CookieCategory {
  id: keyof ConsentPreferences;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  required: boolean;
  examples: string[];
}

interface ConsentPreferences {
  cookiesConsent: boolean;
  functionalConsent: boolean;
  analyticsConsent: boolean;
  marketingConsent: boolean;
}

const cookieCategories: CookieCategory[] = [
  {
    id: 'cookiesConsent',
    icon: Shield,
    title: 'Cookies Essenciais',
    description: 'Necessários para o funcionamento básico do site. Não podem ser desativados.',
    required: true,
    examples: ['Sessão de login', 'Carrinho de compras', 'Segurança CSRF']
  },
  {
    id: 'functionalConsent',
    icon: Cookie,
    title: 'Cookies Funcionais',
    description: 'Melhoram a funcionalidade e personalização do site.',
    required: false,
    examples: ['Preferências de idioma', 'Tema escuro/claro', 'Configurações de interface']
  },
  {
    id: 'analyticsConsent',
    icon: BarChart3,
    title: 'Cookies de Analytics',
    description: 'Ajudam a entender como os visitantes interagem com o site.',
    required: false,
    examples: ['Google Analytics', 'Métricas de performance', 'Comportamento de usuários']
  },
  {
    id: 'marketingConsent',
    icon: Sparkles,
    title: 'Cookies de Marketing',
    description: 'Utilizados para exibir anúncios relevantes aos seus interesses.',
    required: false,
    examples: ['Anúncios personalizados', 'Remarketing', 'Redes sociais']
  }
];

const CookiePreferencesModal = ({ open, onOpenChange }: CookiePreferencesModalProps) => {
  const { preferences, updateConsent, acceptAll } = useConsent();
  const [localPreferences, setLocalPreferences] = useState<ConsentPreferences>({
    cookiesConsent: true,
    functionalConsent: false,
    analyticsConsent: false,
    marketingConsent: false
  });
  const [saving, setSaving] = useState(false);

  // Sincronizar preferências quando abrir o modal
  useEffect(() => {
    if (open && preferences) {
      setLocalPreferences({
        cookiesConsent: preferences.cookiesConsent,
        functionalConsent: preferences.functionalConsent,
        analyticsConsent: preferences.analyticsConsent,
        marketingConsent: preferences.marketingConsent
      });
    }
  }, [open, preferences]);

  const handleToggle = (key: keyof ConsentPreferences) => {
    // Cookies essenciais não podem ser desativados
    if (key === 'cookiesConsent') return;

    setLocalPreferences((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateConsent(localPreferences);
      toast.success('Preferências salvas com sucesso!', {
        description: 'Suas preferências de cookies foram atualizadas.',
        icon: <CheckCircle2 className="h-5 w-5 text-green-500" />
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Erro ao salvar preferências:', error);
      toast.error('Erro ao salvar preferências', {
        description: 'Não foi possível salvar suas preferências. Tente novamente.'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptAll = async () => {
    try {
      setSaving(true);
      await acceptAll();
      toast.success('Todos os cookies aceitos!', {
        description: 'Você aceitou todas as categorias de cookies.',
        icon: <CheckCircle2 className="h-5 w-5 text-green-500" />
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Erro ao aceitar todos:', error);
      toast.error('Erro ao aceitar cookies', {
        description: 'Não foi possível salvar suas preferências. Tente novamente.'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Cookie className="h-6 w-6 text-primary" />
            Preferências de Cookies
          </DialogTitle>
          <DialogDescription>
            Gerencie suas preferências de cookies e privacidade. Você pode escolher quais categorias de cookies deseja
            aceitar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {cookieCategories.map((category, index) => {
            const Icon = category.icon;
            const isEnabled = localPreferences[category.id];

            return (
              <div key={category.id}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex flex-1 gap-4">
                    {/* Ícone */}
                    <div
                      className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg ${
                        category.required || isEnabled ? 'bg-primary/10' : 'bg-muted'
                      }`}
                    >
                      <Icon
                        className={`h-6 w-6 ${
                          category.required || isEnabled ? 'text-primary' : 'text-muted-foreground'
                        }`}
                      />
                    </div>

                    {/* Informações */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Label
                          htmlFor={category.id}
                          className="text-base font-semibold text-foreground cursor-pointer"
                        >
                          {category.title}
                        </Label>
                        {category.required && (
                          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                            Obrigatório
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">{category.description}</p>

                      {/* Exemplos */}
                      <div className="mt-2">
                        <p className="text-xs font-medium text-muted-foreground">Exemplos:</p>
                        <ul className="mt-1 space-y-1">
                          {category.examples.map((example) => (
                            <li key={example} className="flex items-center gap-2 text-xs text-muted-foreground">
                              <div className="h-1 w-1 rounded-full bg-muted-foreground" />
                              {example}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Toggle */}
                  <Switch
                    id={category.id}
                    checked={category.required || isEnabled}
                    onCheckedChange={() => handleToggle(category.id)}
                    disabled={category.required || saving}
                    className="mt-1"
                  />
                </div>

                {/* Separador (exceto no último) */}
                {index < cookieCategories.length - 1 && <Separator className="mt-6" />}
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between">
          <p className="text-xs text-muted-foreground">
            Suas preferências podem ser alteradas a qualquer momento nas configurações.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="outline" onClick={handleAcceptAll} disabled={saving}>
              Aceitar Todos
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar Preferências'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CookiePreferencesModal;
